import {
  Box,
  Button,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useState } from 'react';
import { Dnd, ItemType } from 'src/@carketa/components/dnd/Dnd';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import HomeOutline from 'mdi-material-ui/HomeOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateTwoToneIcon from '@mui/icons-material/AssignmentLateTwoTone';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SellIcon from '@mui/icons-material/Sell';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import { SideMenuValues } from 'src/types/organizations/organizations';

interface CardItemType extends ItemType {
  id: string;
  text: string;
  color: string;
  icon?: any;
}

export const ReorderSideMenuDialog = ({
  open,
  handleClose,
  sideMenuOrder,
  sideMenuOrderData,
}: {
  open: boolean;
  handleClose: () => void;
  sideMenuOrder: (data: any) => void;
  sideMenuOrderData?: SideMenuValues;
}) => {
  const mainSideMenus = [
    {
      id: 'userOrganization',
      text: 'Dashboard',
      color: null,
      icon: <HomeOutline />,
    },
    {
      id: 'userWork',
      text: 'My Work',
      color: null,
      icon: <AssignmentTurnedInIcon />,
    },
    {
      id: 'needsAttention',
      text: 'Needs Attention',
      color: null,
      icon: <AssignmentLateTwoToneIcon />,
    },
    {
      id: 'userInventory',
      text: 'All Inventory',
      color: null,
      icon: <DirectionsCarIcon />,
    },
  ];

  const servicesMenu = [
    {
      id: 'vehicleSourcing',
      text: 'Auto Finder',
      color: null,
      icon: <TroubleshootIcon />,
    },
    {
      id: 'userPricingAppraisals',
      text: 'Appraisals',
      color: null,
      icon: <RequestQuoteIcon />,
    },
    {
      id: 'vehicleTransportation',
      text: 'Transportation',
      color: null,
      icon: <LocalShippingIcon />,
    },
    {
      id: 'userRecon',
      text: 'Recon/Workflow',
      color: null,
      icon: <FormatListBulletedIcon />,
    },
    {
      id: 'userPricingInventory',
      text: 'Inventory Pricing',
      color: null,
      icon: <SellIcon />,
    },
    {
      id: 'userMarketReport',
      text: 'Market Report',
      color: null,
      icon: <BarChartIcon />,
    },
    {
      id: 'userKSLDealerInsights',
      text: 'KSL Dealer Insights',
      color: null,
      icon: <BarChartIcon />,
    },
    {
      id: 'reports',
      text: 'Reports',
      color: null,
      icon: <AssessmentIcon />,
    },
    {
      id: 'userSettings',
      text: 'Settings',
      color: null,
      icon: <SettingsIcon />,
    },
    {
      id: 'addProducts',
      text: 'Add Products',
      icon: <AddCircleOutlineOutlinedIcon />,
      color: null,
    },
  ];

  const sortMenus = (menus: any, defaultMenus: any) => {
    if (!menus) return defaultMenus;

    // Create a mapping of id to order
    const orderMap = menus.reduce((map: any, link: any) => {
      map[link.id] = link.order;
      return map;
    }, {});

    // Sort mainSideMenus based on the orderMap
    const sortedMainSideMenus = defaultMenus.sort(
      (a: any, b: any) => orderMap[a.id] - orderMap[b.id]
    );

    return sortedMainSideMenus;
  };

  const [items, setItems] = useState(
    sortMenus(sideMenuOrderData?.services, servicesMenu)
  );
  const [mainItems, setMainItems] = useState(
    sortMenus(sideMenuOrderData?.mainLinks, mainSideMenus)
  );

  const handleSave = () => {
    const ids = items?.map((item: any, index: number) => {
      return {
        id: item.id,
        order: index,
        name: item.text,
      };
    });

    const mainIds = mainItems?.map((item: any, index: number) => {
      return {
        id: item.id,
        order: index,
        name: item.text,
      };
    });

    sideMenuOrder({
      mainLinks: mainIds,
      services: ids,
    });
    handleClose();
  };

  const theme = useTheme();
  const smBelow = useMediaQuery(theme.breakpoints.down('sm'));

  console.log('mainItems-->', mainItems);

  return (
    <>
      <Dialog fullWidth fullScreen={smBelow} open={open} onClose={handleClose}>
        <DialogTitle>
          Reorder side menu
          <IconButton
            aria-label="close"
            onClick={() => {
              setMainItems(
                sortMenus(
                  sideMenuOrderData ? sideMenuOrderData.mainLinks : null,
                  mainSideMenus
                )
              );
              setItems(
                sortMenus(
                  sideMenuOrderData ? sideMenuOrderData.services : null,
                  servicesMenu
                )
              );
              handleClose();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Dnd
            items={mainItems}
            setItems={(items) => setMainItems(items as CardItemType[])}
            renderContainer={(items: React.ReactNode) => {
              return <List dense>{items}</List>;
            }}
            renderItem={(item: ItemType) => {
              const itemData = item as CardItemType;
              return (
                <ListItem
                  key={itemData.id}
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.light,
                    borderRadius: 1.1,
                    cursor: 'pointer',
                    marginBottom: 2,
                  }}
                  dense
                >
                  <ListItemIcon>
                    <Box sx={{ color: 'white' }}>
                      <DragHandleIcon />
                    </Box>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography sx={{ color: 'white' }}>
                      {itemData.icon} {itemData.text}
                    </Typography>
                  </ListItemText>
                </ListItem>
              );
            }}
          />
          <Divider textAlign="left" sx={{ color: '#B9B7BE' }}>
            Services
          </Divider>
          <Dnd
            items={items}
            setItems={(items) => setItems(items as CardItemType[])}
            renderContainer={(items: React.ReactNode) => {
              return <List dense>{items}</List>;
            }}
            renderItem={(item: ItemType) => {
              const itemData = item as CardItemType;
              return (
                <ListItem
                  key={itemData.id}
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.light,
                    borderRadius: 1.1,
                    cursor: 'pointer',
                    marginBottom: 2,
                  }}
                  dense
                >
                  <ListItemIcon>
                    <Box sx={{ color: 'white' }}>
                      <DragHandleIcon />
                    </Box>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography sx={{ color: 'white' }}>
                      {itemData.icon} {itemData.text}
                    </Typography>
                  </ListItemText>
                </ListItem>
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setMainItems(
                sortMenus(
                  sideMenuOrderData ? sideMenuOrderData.mainLinks : null,
                  mainSideMenus
                )
              );
              setItems(
                sortMenus(
                  sideMenuOrderData ? sideMenuOrderData.services : null,
                  servicesMenu
                )
              );
              handleClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSave();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
